import { FunctionComponent, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import moment from 'moment';
import Button from '../../../../components/Button';
import Text from '../../../../components/Text';
import TooltipWrapper from '../../../../components/TooltipWrapper';
import SkeletonBone, { SkeletonStack } from '../../../../components/SkeletonBone';
import { styles } from '../../../../styles/themes/style';
import { useCampaignsRequest } from '../../../../services/apis/campaigns/useCampaignsRequest';
import { useAppActions } from '../../../../stores/application/useAppActions';
import { useAppHandlers } from '../../../../services/actions/app/useAppHandlers';
import LeadsPagination from '../campaigns/LeadsPagination';
import {
  ISuppressionEntry, ISuppressionList, ISuppressionListResponse, SuppressionType,
} from '../../../../interfaces/models/campaign';
import { describeListScope } from '../../../../utils/suppressionListUtils';

/**
 * Settings → Campaign settings → Suppression lists.
 *
 * People and companies campaigns may not contact: LinkedIn profiles, company domains and
 * email addresses, kept in named lists. Each list binds some campaigns — every campaign,
 * every campaign but its owner's, or the campaigns that pick it. The API skips listed
 * people at every import and takes matching leads out of running campaigns the moment
 * they are added. Adding happens in AddToSuppressionListModal, which asks first whenever
 * it would remove someone; lists are made and changed in SuppressionListFormModal.
 *
 * The email channel's own list (unsubscribes, bounces) is shown on the workspace list,
 * marked "Email only": those addresses are never emailed but may still be reached on LinkedIn.
 *
 * Everyone can read every list and add to most; the API says per list and per row what
 * this viewer may change (canAdd, canManage, removable).
 */

const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;
/** Value widths of the placeholder rows, uneven so they read as text. */
const SKELETON_WIDTHS = ['46%', '34%', '52%', '40%'];

const FILTER_LABELS: Record<SuppressionType, string> = {
  profile: 'LinkedIn profiles',
  domain: 'Company domains',
  email: 'Email addresses',
};

const TYPE_NAMES: Record<SuppressionType, string> = {
  profile: 'LinkedIn profile',
  domain: 'Company domain',
  email: 'Email address',
};

const TYPE_TONES: Record<SuppressionType, { bg: string; fg: string }> = {
  profile: { bg: '#e8f1fb', fg: '#0a66c2' },
  domain: { bg: styles.colors.primary100, fg: styles.colors.primary600 },
  email: { bg: styles.colors.yellow200, fg: '#c48a00' },
};

const ICON_PATHS = {
  profile: <><circle cx="12" cy="8" r="4" /><path d="M4.5 20c0-3.6 3.4-6 7.5-6s7.5 2.4 7.5 6" /></>,
  domain: <><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c2.4 2.6 3.6 5.6 3.6 9s-1.2 6.4-3.6 9c-2.4-2.6-3.6-5.6-3.6-9S9.6 5.6 12 3z" /></>,
  email: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3.5 6.5 8.5 6.5 8.5-6.5" /></>,
  trash: <><path d="M4 7h16M10 11v6M14 11v6M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12M9 7V4h6v3" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-3.5-3.5" /></>,
  external: <><path d="M14 4h6v6M20 4l-9 9M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5" /></>,
  info: <><circle cx="12" cy="12" r="9" /><path d="M12 11v5M12 8h.01" /></>,
  scopeAll: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>,
  scopeOthers: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  scopeSelected: <><path d="M9 6h11M9 12h11M9 18h11" /><path d="m3 6 1 1 2-2M3 12l1 1 2-2M3 18l1 1 2-2" /></>,
  plus: <><path d="M12 5v14M5 12h14" /></>,
};

const SCOPE_ICONS: Record<ISuppressionList['scope'], keyof typeof ICON_PATHS> = {
  all: 'scopeAll',
  others: 'scopeOthers',
  selected: 'scopeSelected',
};

/** Many leads carry no website or email, only the company name LinkedIn shows. */
function companyMatchHelp(entry: ISuppressionEntry): string {
  const company = entry.companyName ? `“${entry.companyName}”` : 'the company behind this domain';
  return `Many leads have no website or email, only a company name. With this on, a lead whose company is `
    + `${company} counts as ${entry.value} too. A different company with exactly the same name would be `
    + 'matched as well, so it is off by default.';
}

const Icon: FunctionComponent<{ name: keyof typeof ICON_PATHS; size?: number }> = ({ name, size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {ICON_PATHS[name]}
  </svg>
);

/** Reasons on the email channel's list, as the inbound-mail webhook records them. */
const EMAIL_REASON_LABELS: Record<string, string> = {
  unsubscribe: 'Unsubscribed',
  complaint: 'Spam complaint',
  hard_bounce: 'Hard bounce',
  invalid: 'Invalid address',
  manual: 'Do not email',
};

function plural(n: number, word: string, many = `${word}s`): string {
  return `${n.toLocaleString()} ${n === 1 ? word : many}`;
}

/** Sales Navigator ids (ACw…) have no public profile URL to link to. */
function profileHref(value: string): string | undefined {
  return /^acw/i.test(value) ? undefined : `https://www.linkedin.com/in/${encodeURIComponent(value)}`;
}

/** The scope in a few words, for the list column. */
function shortScope(list: ISuppressionList, userId?: string): string {
  if (list.scope === 'all') return 'Every campaign';
  if (list.scope === 'others') {
    return list.owner && String(list.owner._id) === String(userId) ? 'Reserved for you' : `Reserved for ${list.owner?.name || 'a member'}`;
  }
  return 'Selected campaigns';
}

const SuppressionList: FunctionComponent = () => {
  const api = useCampaignsRequest();
  // The request helpers are new functions every render; reading them through a ref keeps
  // them out of effect dependencies, which would otherwise refetch on every render.
  const apiRef = useRef(api);
  apiRef.current = api;
  const { setToast } = useAppActions();
  const {
    setConfirmationModal, setDestructiveModal, setAddToSuppressionListModal, setSuppressionListFormModal, hideModal,
  } = useAppHandlers();

  // The open list lives in the URL (?list=), so a link can point at one.
  const [searchParams, setSearchParams] = useSearchParams();
  const listParam = searchParams.get('list') || '';
  const [lists, setLists] = useState<ISuppressionList[] | null>(null);
  const [userId, setUserId] = useState<string>();
  const [listsKey, setListsKey] = useState(0);

  const [data, setData] = useState<ISuppressionListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState<SuppressionType | ''>('');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [reloadKey, setReloadKey] = useState(0);

  const [busyId, setBusyId] = useState<string | null>(null);
  /** Domain row whose company name is being typed by hand (lookup found none, or editing). */
  const [nameEdit, setNameEdit] = useState<{ id: string; value: string } | null>(null);

  const selected = lists?.find(l => l._id === listParam) ?? lists?.find(l => l.isDefault) ?? null;
  const listId = selected?._id ?? '';
  const canAdd = !!selected?.canAdd;
  const reload = () => setReloadKey(k => k + 1);
  const reloadLists = () => setListsKey(k => k + 1);

  useEffect(() => {
    let stale = false;
    void apiRef.current.getSuppressionLists().then(res => {
      if (stale) return;
      if (res?.success) {
        setLists(res.data.lists);
        setUserId(res.data.userId);
      } else {
        setToast({ type: 'error', message: res?.message || 'Could not load the suppression lists.' });
      }
    });
    return () => { stale = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listsKey]);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Requests share one slug, so starting a load aborts the one in flight, and the aborted
  // one resolves with no response. Only the newest load may touch state — otherwise every
  // overlap (StrictMode's double mount, a filter change during a reload) reads as an error.
  useEffect(() => {
    if (!listId) return;
    let stale = false;
    setLoading(true);
    void apiRef.current.getSuppressions({ listId, type: typeFilter, search, page, limit: pageSize }).then(res => {
      if (stale) return;
      setLoading(false);
      if (res?.success) setData(res.data);
      else setToast({ type: 'error', message: res?.message || 'Could not load the suppression list.' });
    });
    return () => { stale = true; };
    // setToast is stable for the lifetime of the store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listId, typeFilter, search, page, pageSize, reloadKey]);

  /** Open a list with its filters cleared. Its rows are dropped so the skeleton shows, not the last list's. */
  const selectList = (id: string) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.set('list', id);
      return next;
    }, { replace: true });
    setData(null);
    setTypeFilter('');
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  // ─── Lists ────────────────────────────────────────────────────────────────

  const openCreate = () => setSuppressionListFormModal({
    onSaved: id => { reloadLists(); selectList(id); },
  });

  const openEdit = (list: ISuppressionList) => setSuppressionListFormModal({
    list,
    onSaved: () => { reloadLists(); reload(); },
  });

  const confirmDelete = (list: ISuppressionList) => {
    setDestructiveModal({
      headingText: `Delete “${list.name}”?`,
      descriptionText: `Its ${plural(list.entryCount, 'entry', 'entries')} go with it, and campaigns that turned it on stop applying it. `
        + 'Leads it already took out stay out — retry them from their campaign to bring them back.',
      confirmBtnTitle: 'Delete list',
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        const res = await apiRef.current.deleteSuppressionList(list._id);
        if (!res?.success) {
          setToast({ type: 'error', message: res?.message || 'Could not delete the list.' });
          return;
        }
        setToast({ type: 'success', message: `List “${list.name}” deleted.` });
        const fallback = lists?.find(l => l.isDefault);
        if (fallback) selectList(fallback._id);
        reloadLists();
      },
    });
  };

  // ─── Add ──────────────────────────────────────────────────────────────────

  /** New entries go to the top of the unfiltered list they were added to, so show that. */
  const openAdd = () => setAddToSuppressionListModal({
    listId,
    onAdded: addedTo => {
      if (addedTo && addedTo !== listId) selectList(addedTo);
      else {
        setTypeFilter('');
        setSearchInput('');
        setSearch('');
        setPage(1);
      }
      reload();
      reloadLists();
    },
  });

  // ─── Row actions ──────────────────────────────────────────────────────────

  const handleRemove = (entry: ISuppressionEntry) => {
    setDestructiveModal({
      headingText: 'Remove from the suppression list?',
      descriptionText: entry.list === 'contact'
        ? `${entry.value} will no longer be skipped. Leads it already took out stay out — retry them from their campaign to bring them back.`
        : `${entry.value} can be emailed again, and leads parked on this address get it back.`,
      confirmBtnTitle: 'Remove',
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        setBusyId(entry._id);
        const res = await apiRef.current.removeSuppression(entry._id);
        setBusyId(null);
        if (res?.success) {
          setToast({ type: 'success', message: `${entry.value} removed from the suppression list.` });
          reload();
          reloadLists();
        } else {
          setToast({ type: 'error', message: res?.message || 'Could not remove this entry.' });
        }
      },
    });
  };

  const turnOffCompanyMatch = async (entry: ISuppressionEntry) => {
    setBusyId(entry._id);
    const res = await apiRef.current.updateSuppression(entry._id, { matchCompanyName: false });
    setBusyId(null);
    if (res?.success) reload();
    else setToast({ type: 'error', message: res?.message || 'Could not update this entry.' });
  };

  /**
   * Two steps: a dry run resolves the company name (or takes the one typed) and counts
   * the leads it would take out, then the user confirms with both in front of them.
   */
  const turnOnCompanyMatch = async (entry: ISuppressionEntry, typedName?: string) => {
    setBusyId(entry._id);
    const dry = await apiRef.current.updateSuppression(entry._id, {
      matchCompanyName: true,
      dryRun: true,
      ...(typedName && { companyName: typedName }),
    });
    setBusyId(null);

    if (!dry?.success) {
      setToast({ type: 'warning', message: dry?.message || 'Could not find the company for this domain.' });
      setNameEdit({ id: entry._id, value: typedName ?? '' });
      return;
    }

    const { companyName, leadsAffected, campaignsAffected } = dry.data as {
      companyName: string; leadsAffected: number; campaignsAffected: number;
    };
    setConfirmationModal({
      headingText: `Also match the company name "${companyName}"?`,
      descriptionText:
        `Leads whose company is "${companyName}" count as ${entry.value} even without a website or email — `
        + `${plural(leadsAffected, 'lead')} in ${plural(campaignsAffected, 'running campaign')} would be taken out now. `
        + 'A different company with exactly the same name would be matched too.',
      confirmBtnTitle: 'Match company name',
      onCancelHandler: () => hideModal(),
      onConfirmHandler: async () => {
        hideModal();
        setBusyId(entry._id);
        const res = await apiRef.current.updateSuppression(entry._id, { matchCompanyName: true, companyName });
        setBusyId(null);
        if (res?.success) {
          setNameEdit(null);
          const removed = res.data?.leadsRemoved ?? 0;
          setToast({
            type: 'success',
            message: removed
              ? `Matching "${companyName}" — ${plural(removed, 'lead')} removed.`
              : `Matching "${companyName}".`,
          });
          reload();
        } else {
          setToast({ type: 'error', message: res?.message || 'Could not update this entry.' });
        }
      },
    });
  };

  // ─── Render ───────────────────────────────────────────────────────────────

  const rows = data?.suppressions ?? [];
  const counts = data?.counts ?? { profile: 0, domain: 0, email: 0 };
  const allCount = counts.profile + counts.domain + counts.email;
  const total = data?.total ?? 0;
  const filtered = !!(search || typeFilter);

  const renderRow = (entry: ISuppressionEntry) => {
    const busy = busyId === entry._id;
    const href = entry.type === 'profile' ? profileHref(entry.value) : undefined;
    const meta = [
      TYPE_NAMES[entry.type],
      entry.source === 'lead' ? 'added from a campaign lead' : '',
      entry.leadsRemoved ? `${plural(entry.leadsRemoved, 'lead')} taken out of campaigns` : '',
    ].filter(Boolean);
    const editingName = nameEdit?.id === entry._id;

    return (
      <EntryRow key={`${entry.list}-${entry._id}`} $busy={busy}>
        <TypeIcon $type={entry.type} title={TYPE_NAMES[entry.type]}>
          <Icon name={entry.type} size={18} />
        </TypeIcon>

        <EntryMain>
          <EntryTop>
            {href ? (
              <EntryLink href={href} target="_blank" rel="noopener noreferrer" title="Open on LinkedIn">
                {entry.value}
                <Icon name="external" size={12} />
              </EntryLink>
            ) : <EntryValue>{entry.value}</EntryValue>}
            {/* Everything else applies on every channel; only the email list's entries don't. */}
            {entry.scope === 'email' && (
              <ScopeChip>Email only · {EMAIL_REASON_LABELS[entry.reason] ?? entry.reason}</ScopeChip>
            )}
          </EntryTop>
          <EntryMeta>{meta.join(' · ')}</EntryMeta>

          {/* Whoever may remove the entry may also widen it to the company name. */}
          {entry.type === 'domain' && entry.list === 'contact' && (entry.removable || entry.matchCompanyName) && (
            <CompanyMatchRow>
              <SwitchBtn
                type="button"
                $on={!!entry.matchCompanyName}
                disabled={!entry.removable || busy}
                onClick={() => (entry.matchCompanyName ? turnOffCompanyMatch(entry) : turnOnCompanyMatch(entry))}
                aria-pressed={!!entry.matchCompanyName}
              >
                <SwitchKnob $on={!!entry.matchCompanyName} />
              </SwitchBtn>
              <CompanyMatchText>
                Also match company name
                {entry.matchCompanyName && entry.companyName && <strong> “{entry.companyName}”</strong>}
              </CompanyMatchText>
              <TooltipWrapper placement="top" textAlignment="left" tooltipText={companyMatchHelp(entry)}>
                <InfoIcon tabIndex={0} aria-label="What does this do?">
                  <Icon name="info" size={14} />
                </InfoIcon>
              </TooltipWrapper>
              {entry.removable && entry.matchCompanyName && !editingName && (
                <LinkBtn type="button" onClick={() => setNameEdit({ id: entry._id, value: entry.companyName || '' })}>
                  Change name
                </LinkBtn>
              )}
            </CompanyMatchRow>
          )}

          {editingName && (
            <NameEditRow>
              <SmallInput
                autoFocus
                placeholder="Company name, as it appears on LinkedIn"
                value={nameEdit!.value}
                onChange={e => setNameEdit({ id: entry._id, value: e.target.value })}
                onKeyDown={e => { if (e.key === 'Enter' && nameEdit!.value.trim()) void turnOnCompanyMatch(entry, nameEdit!.value.trim()); }}
              />
              <Button
                $type="blue"
                $width="80px"
                disabled={!nameEdit!.value.trim() || busy}
                onClickHandler={() => turnOnCompanyMatch(entry, nameEdit!.value.trim())}
              >
                Check
              </Button>
              <LinkBtn type="button" onClick={() => setNameEdit(null)}>Cancel</LinkBtn>
            </NameEditRow>
          )}
        </EntryMain>

        <ReasonCell $empty={!entry.note} title={entry.note || undefined}>
          {entry.note ? <><ReasonLabel>Reason: </ReasonLabel>{entry.note}</> : '—'}
        </ReasonCell>

        <AddedCell>
          <span>{moment(entry.createdAt).format('MMM D, YYYY')}</span>
          {entry.createdBy?.name && <small>by {entry.createdBy.name}</small>}
        </AddedCell>

        <ActionCell>
          {entry.removable && (
            <RemoveBtn
              type="button"
              disabled={busy}
              onClick={() => handleRemove(entry)}
              title="Remove from the list"
              aria-label={`Remove ${entry.value} from the list`}
            >
              <Icon name="trash" size={17} />
            </RemoveBtn>
          )}
        </ActionCell>
      </EntryRow>
    );
  };

  // ─── Loading ──────────────────────────────────────────────────────────────

  const renderSkeletonRows = () => (
    <EntryList $loading={false} aria-busy="true">
      <ListHead>
        <span>Entry</span>
        <span>Reason</span>
        <span>Added</span>
      </ListHead>
      {SKELETON_WIDTHS.map((width, i) => (
        <EntryRow key={i} $busy={false}>
          <SkeletonBone $w="36px" $h="36px" $radius="10px" />
          <SkeletonStack>
            <SkeletonBone $w={width} $h="14px" />
            <SkeletonBone $w="26%" $h="11px" />
          </SkeletonStack>
          <ReasonCell $empty>
            <SkeletonBone $w={i % 2 ? '40%' : '64%'} $h="12px" />
          </ReasonCell>
          <SkeletonStack>
            <SkeletonBone $w="84px" $h="12px" />
            <SkeletonBone $w="58px" $h="10px" />
          </SkeletonStack>
          <span />
        </EntryRow>
      ))}
    </EntryList>
  );

  const renderSkeletonNav = () => ['62%', '48%'].map((width, i) => (
    <SkeletonListItem key={i} aria-hidden>
      <SkeletonBone $w="30px" $h="30px" $radius="8px" />
      <SkeletonStack $gap="5px">
        <SkeletonBone $w={width} $h="13px" />
        <SkeletonBone $w="44%" $h="10px" />
      </SkeletonStack>
      <SkeletonBone $w="24px" $h="20px" $radius="10px" />
    </SkeletonListItem>
  ));

  const renderSkeletonHead = () => (
    <SkeletonStack $gap="8px" aria-busy="true">
      <SkeletonBone $w="160px" $h="20px" />
      <SkeletonBone $w="230px" $h="12px" />
    </SkeletonStack>
  );

  const renderSkeletonFilters = () => (
    <FilterBar aria-hidden>
      <SkeletonBone $w="min(440px, 100%)" $h="36px" $radius="9px" />
      <SkeletonBone $w="min(260px, 100%)" $h="36px" $radius="8px" />
    </FilterBar>
  );

  const renderList = () => {
    if (rows.length) {
      return (
        <EntryList $loading={loading}>
          <ListHead>
            <span>Entry</span>
            <span>Reason</span>
            <span>Added</span>
          </ListHead>
          {rows.map(renderRow)}
        </EntryList>
      );
    }
    // First load of a list: rows in outline, not a flash of the empty state.
    if (loading) return renderSkeletonRows();
    return (
      <EmptyState>
        <EmptyIcon aria-hidden><Icon name={filtered ? 'search' : 'scopeSelected'} size={20} /></EmptyIcon>
        <EmptyTitle>{filtered ? 'Nothing matches' : 'The list is empty'}</EmptyTitle>
        <EmptyText>
          {filtered
            ? 'Try another search or type.'
            : canAdd
              ? 'Add the people and companies these campaigns should never reach — customers, partners, competitors.'
              : 'Nobody is on this list yet.'}
        </EmptyText>
        {canAdd && !filtered && (
          <Button
            $type="blue"
            $width="132px"
            $SVGtype="plus"
            $iconColor={styles.colors.white}
            tooltipMessage=""
            onClickHandler={openAdd}
          >
            Add to list
          </Button>
        )}
      </EmptyState>
    );
  };

  // One panel, master–detail: the two header cells share a grid row, so they are always
  // the same height and the lists line up with the entries they open.
  return (
    <Container>
      <Text $lighter>
        Lists of people and companies your campaigns must not contact. They are skipped at every import,
        and adding someone takes them out of running campaigns straight away.
      </Text>

      <Panel>
        <NavHead>
          <NavTitle>
            Lists
            {!!lists?.length && <PaneCount>{lists.length}</PaneCount>}
          </NavTitle>
          <NewListBtn type="button" onClick={openCreate}>
            <Icon name="plus" size={14} />
            New list
          </NewListBtn>
        </NavHead>

        <MainHead>
          {!lists && renderSkeletonHead()}
          {selected && (
            <>
              <Header>
                <ListTitle>{selected.name}</ListTitle>
                <ListSub>
                  {describeListScope(selected, userId)}
                  {selected.owner?.name && selected.scope !== 'others' ? ` · created by ${selected.owner.name}` : ''}
                </ListSub>
              </Header>
              <HeaderActions>
                {selected.canManage && (
                  <>
                    <GhostBtn type="button" onClick={() => openEdit(selected)}>Edit</GhostBtn>
                    <GhostBtn type="button" $danger onClick={() => confirmDelete(selected)}>Delete</GhostBtn>
                  </>
                )}
                {canAdd && (
                  <Button
                    $type="blue"
                    $width="132px"
                    $SVGtype="plus"
                    $iconColor={styles.colors.white}
                    tooltipMessage=""
                    onClickHandler={openAdd}
                  >
                    Add to list
                  </Button>
                )}
              </HeaderActions>
            </>
          )}
        </MainHead>

        <Nav aria-busy={!lists}>
          {!lists && renderSkeletonNav()}
          {(lists ?? []).map(list => {
            const active = list._id === listId;
            return (
              <ListItem
                key={list._id}
                type="button"
                $active={active}
                aria-current={active ? 'page' : undefined}
                onClick={() => !active && selectList(list._id)}
              >
                <ListIcon $active={active} aria-hidden>
                  <Icon name={SCOPE_ICONS[list.scope]} size={15} />
                </ListIcon>
                <ListText>
                  <ListName $active={active}>{list.name}</ListName>
                  <ListScope>{shortScope(list, userId)}</ListScope>
                </ListText>
                <ListCount $active={active}>{list.entryCount.toLocaleString()}</ListCount>
              </ListItem>
            );
          })}
        </Nav>

        <MainBody>
          {!lists && (
            <>
              {renderSkeletonFilters()}
              {renderSkeletonRows()}
            </>
          )}
          {selected && (
          <>
            {!canAdd && (
              <ReadOnlyNote>
                {selected.owner?.name || 'Its owner'} keeps these contacts for their own campaigns. Only they or an admin can add to this list.
              </ReadOnlyNote>
            )}

            {!data ? renderSkeletonFilters() : (
            <FilterBar>
              <TypeTabs role="tablist" aria-label="Entry type">
                <TypeTab type="button" role="tab" aria-selected={typeFilter === ''} $active={typeFilter === ''} onClick={() => { setTypeFilter(''); setPage(1); }}>
                  All <Count $active={typeFilter === ''}>{allCount.toLocaleString()}</Count>
                </TypeTab>
                {(['profile', 'domain', 'email'] as SuppressionType[]).map(t => (
                  <TypeTab key={t} type="button" role="tab" aria-selected={typeFilter === t} $active={typeFilter === t} onClick={() => { setTypeFilter(t); setPage(1); }}>
                    {FILTER_LABELS[t]} <Count $active={typeFilter === t}>{counts[t].toLocaleString()}</Count>
                  </TypeTab>
                ))}
              </TypeTabs>
              <SearchWrap>
                <Icon name="search" />
                <SearchInput
                  placeholder="Search entries"
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                />
              </SearchWrap>
            </FilterBar>
            )}

            <div>
              {renderList()}
              {total > 0 && (
                <LeadsPagination
                  page={page}
                  pageSize={pageSize}
                  total={total}
                  loading={loading}
                  onPageChange={setPage}
                  onPageSizeChange={size => { setPageSize(size); setPage(1); }}
                />
              )}
            </div>
          </>
          )}
        </MainBody>
      </Panel>
    </Container>
  );
};

// A tab of Campaign settings, which owns the padding and the scrolling.
const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const NAV_BG = '#fafbfd';
const NARROW = '@media (max-width: 1000px)';

// Lists on the left, the open list on the right, in one bordered panel; stacked when narrow.
const Panel = styled.div`
  display: grid;
  grid-template-columns: 260px minmax(0, 1fr);
  grid-template-rows: auto 1fr;
  grid-template-areas:
    'navhead mainhead'
    'nav     main';
  border: 1px solid ${styles.colors.black200};
  border-radius: 12px;
  background: ${styles.colors.white};
  overflow: hidden;

  ${NARROW} {
    grid-template-columns: minmax(0, 1fr);
    grid-template-areas: 'navhead' 'nav' 'mainhead' 'main';
  }
`;

const NavHead = styled.div`
  grid-area: navhead;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 16px 12px 16px 16px;
  border-right: 1px solid ${styles.colors.black200};
  border-bottom: 1px solid ${styles.colors.black200};
  background: ${NAV_BG};

  ${NARROW} { border-right: none; }
`;

const NavTitle = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-family: NunitoSans;
  font-size: 15px;
  font-weight: 800;
  color: ${styles.colors.black600};
`;

const PaneCount = styled.span`
  min-width: 22px;
  height: 20px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: ${styles.colors.black100};
  font-size: 12px;
  font-weight: 700;
  color: ${styles.colors.black400};
`;

const MainHead = styled.div`
  grid-area: mainhead;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px 16px;
  padding: 16px 20px;
  border-bottom: 1px solid ${styles.colors.black200};
`;

const Nav = styled.nav`
  grid-area: nav;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
  border-right: 1px solid ${styles.colors.black200};
  background: ${NAV_BG};

  ${NARROW} { border-right: none; border-bottom: 1px solid ${styles.colors.black200}; }
`;

const MainBody = styled.section`
  grid-area: main;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px 20px 20px;
`;

const ListSub = styled.span`
  font-family: NunitoSans;
  font-size: 13px;
  color: ${styles.colors.black400};
`;

const ListItem = styled.button<{ $active: boolean }>`
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px;
  border: none;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? styles.colors.primary100 : 'transparent')};
  font-family: NunitoSans;
  text-align: left;
  cursor: ${({ $active }) => ($active ? 'default' : 'pointer')};
  transition: background 0.12s;

  &:hover { background: ${({ $active }) => ($active ? styles.colors.primary100 : '#f0f3f8')}; }
`;

const ListIcon = styled.span<{ $active: boolean }>`
  width: 30px;
  height: 30px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
  background: ${({ $active }) => ($active ? styles.colors.white : styles.colors.black100)};
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black400)};
  transition: background 0.12s, color 0.12s;
`;

const ListText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
`;

const ListName = styled.span<{ $active: boolean }>`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
  font-weight: 700;
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black600)};
`;

const ListScope = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12px;
  color: ${styles.colors.black400};
`;

const ListCount = styled.span<{ $active: boolean }>`
  min-width: 24px;
  height: 20px;
  padding: 0 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: ${({ $active }) => ($active ? styles.colors.white : styles.colors.black100)};
  font-size: 12px;
  font-weight: 700;
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black400)};
`;

const NewListBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  height: 30px;
  padding: 0 10px;
  border: 1px solid ${styles.colors.black200};
  border-radius: 6px;
  background: ${styles.colors.white};
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  color: ${styles.colors.primary600};
  cursor: pointer;
  transition: background 0.12s, border-color 0.12s;

  &:hover { border-color: ${styles.colors.primary200}; background: ${styles.colors.primary100}; }
`;

const ListTitle = styled.span`
  font-family: NunitoSans;
  font-size: 18px;
  font-weight: 800;
  color: ${styles.colors.black600};
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GhostBtn = styled.button<{ $danger?: boolean }>`
  height: 34px;
  padding: 0 12px;
  border: 1px solid ${styles.colors.black200};
  border-radius: 6px;
  background: ${styles.colors.white};
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  color: ${({ $danger }) => ($danger ? styles.colors.red600 : styles.colors.black500)};
  cursor: pointer;

  &:hover {
    border-color: ${({ $danger }) => ($danger ? styles.colors.red400 : styles.colors.primary200)};
    background: ${({ $danger }) => ($danger ? styles.colors.red200 : styles.colors.white)};
  }
`;

// Takes the row up to the buttons.
const Header = styled.div`
  flex: 1 1 260px;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

// A segmented control, like Sending accounts' sections: one choice out of four, and
// lighter than a row of bordered pills each carrying its own badge. Scrolls sideways
// rather than wrapping when the panel is narrow.
const TypeTabs = styled.div`
  display: inline-flex;
  max-width: 100%;
  gap: 2px;
  padding: 3px;
  border-radius: 9px;
  background: #f1f3f8;
  overflow-x: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar { display: none; }
`;

const TypeTab = styled.button<{ $active: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  height: 30px;
  padding: 0 8px 0 12px;
  /* Centred by the flex box, not by font metrics, so label and count sit on one line. */
  line-height: 1;
  border: none;
  border-radius: 7px;
  background: ${({ $active }) => ($active ? styles.colors.white : 'transparent')};
  box-shadow: ${({ $active }) => ($active ? '0 1px 2px rgba(16, 24, 40, 0.08), 0 1px 3px rgba(16, 24, 40, 0.06)' : 'none')};
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black400)};
  font-family: NunitoSans;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition: background 0.14s, color 0.14s, box-shadow 0.14s;

  &:hover { color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black600)}; }
  &:focus-visible { outline: 2px solid ${styles.colors.primary200}; outline-offset: 1px; }
`;

// A small counter chip, the same height in every tab so the row lines up. Zero keeps
// full contrast: "none of this type" is information too.
const Count = styled.span<{ $active: boolean }>`
  min-width: 20px;
  height: 18px;
  padding: 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  border-radius: 9px;
  background: ${({ $active }) => ($active ? styles.colors.primary100 : '#e3e7ef')};
  color: ${({ $active }) => ($active ? styles.colors.primary600 : styles.colors.black500)};
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
`;

const SmallInput = styled.input`
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0 10px;
  border: 1px solid ${styles.colors.black200};
  border-radius: 6px;
  font-family: NunitoSans;
  font-size: 14px;

  &:focus { outline: none; border-color: ${styles.colors.primary600}; }
`;

const ReadOnlyNote = styled.div`
  padding: 10px 12px;
  border-radius: 6px;
  background: ${styles.colors.primary100};
  color: ${styles.colors.black500};
  font-size: 13px;
`;

const FilterBar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
`;

const SearchWrap = styled.div`
  position: relative;
  flex: 0 1 260px;
  display: flex;
  color: ${styles.colors.black300};

  svg {
    position: absolute;
    left: 11px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
  }
`;

const SearchInput = styled(SmallInput)`
  height: 36px;
  padding-left: 34px;
  border-radius: 8px;
  color: ${styles.colors.black600};

  &::placeholder { color: ${styles.colors.black300}; }
`;

// Same tracks as ListItem, without its hover and pointer.
const SkeletonListItem = styled.div`
  display: grid;
  grid-template-columns: 30px minmax(0, 1fr) auto;
  align-items: center;
  gap: 10px;
  padding: 8px;
`;

// Inside the panel already, so a soft fill rather than another border.
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 36px 20px;
  border-radius: 10px;
  background: ${NAV_BG};
  text-align: center;

  button { margin-top: 6px; }
`;

const EmptyIcon = styled.span`
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${styles.colors.primary100};
  color: ${styles.colors.primary600};
`;

const EmptyTitle = styled.span`
  font-family: NunitoSans;
  font-size: 16px;
  font-weight: 800;
  color: ${styles.colors.black600};
`;

const EmptyText = styled.span`
  max-width: 420px;
  font-family: NunitoSans;
  font-size: 13px;
  line-height: 19px;
  color: ${styles.colors.black400};
`;

/** Type icon · entry · reason · added · remove. The head row shares the tracks. */
const ROW_COLUMNS = '36px minmax(0, 1fr) minmax(120px, 0.7fr) 150px 32px';
const MOBILE = '@media (max-width: 768px)';

const EntryList = styled.div<{ $loading: boolean }>`
  display: flex;
  flex-direction: column;
  border: 1px solid ${styles.colors.black200};
  border-radius: 10px;
  overflow: hidden;
  background: ${styles.colors.white};
  opacity: ${({ $loading }) => ($loading ? 0.5 : 1)};
  transition: opacity 0.15s ease;
`;

const ListHead = styled.div`
  display: grid;
  grid-template-columns: ${ROW_COLUMNS};
  column-gap: 14px;
  padding: 9px 16px;
  background: ${styles.colors.black100};
  border-bottom: 1px solid ${styles.colors.black200};
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: ${styles.colors.black300};

  span:first-child { grid-column: 1 / 3; }

  ${MOBILE} { display: none; }
`;

const EntryRow = styled.div<{ $busy: boolean }>`
  display: grid;
  grid-template-columns: ${ROW_COLUMNS};
  align-items: center;
  column-gap: 14px;
  row-gap: 6px;
  padding: 14px 16px;
  opacity: ${({ $busy }) => ($busy ? 0.5 : 1)};
  transition: background 0.12s ease;

  &:hover { background: #fafbfd; }
  & + & { border-top: 1px solid ${styles.colors.black200}; }

  ${MOBILE} { grid-template-columns: 36px minmax(0, 1fr) 32px; }
`;

const TypeIcon = styled.span<{ $type: SuppressionType }>`
  width: 36px;
  height: 36px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  background: ${({ $type }) => TYPE_TONES[$type].bg};
  color: ${({ $type }) => TYPE_TONES[$type].fg};

  ${MOBILE} { align-self: start; }
`;

const EntryMain = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const EntryTop = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
`;

const EntryValue = styled.span`
  font-size: 14px;
  font-weight: 700;
  color: ${styles.colors.black600};
  word-break: break-all;
`;

const EntryLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 14px;
  font-weight: 700;
  color: ${styles.colors.black600};
  word-break: break-all;
  text-decoration: none;

  svg { flex-shrink: 0; color: ${styles.colors.black300}; opacity: 0; transition: opacity 0.12s; }
  &:hover { color: ${styles.colors.primary600}; }
  &:hover svg { opacity: 1; color: ${styles.colors.primary600}; }
`;

const ScopeChip = styled.span`
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 12px;
  font-weight: 600;
  background: #e8f4f6;
  color: #4d8f9e;
`;

const EntryMeta = styled.div`
  font-size: 12px;
  color: ${styles.colors.black400};
`;

// Two lines at most; the whole reason is in the hover title.
const ReasonCell = styled.div<{ $empty: boolean }>`
  min-width: 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  font-size: 13px;
  line-height: 18px;
  color: ${({ $empty }) => ($empty ? styles.colors.black300 : styles.colors.black500)};
  overflow-wrap: anywhere;

  ${MOBILE} {
    grid-column: 2;
    grid-row: 3;
    font-size: 12px;
    ${({ $empty }) => $empty && 'display: none;'}
  }
`;

// The column header names it on wider screens; stacked on a phone, the cell does.
const ReasonLabel = styled.span`
  display: none;
  color: ${styles.colors.black400};

  ${MOBILE} { display: inline; }
`;

const AddedCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1px;
  font-size: 13px;
  color: ${styles.colors.black500};

  small { font-size: 12px; color: ${styles.colors.black300}; }

  ${MOBILE} {
    grid-column: 2;
    grid-row: 2;
    flex-direction: row;
    gap: 4px;
    font-size: 12px;
  }
`;

const ActionCell = styled.div`
  display: flex;
  justify-content: flex-end;

  ${MOBILE} { grid-column: 3; grid-row: 1; }
`;

const CompanyMatchRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
`;

const CompanyMatchText = styled.span`
  font-size: 13px;
  color: ${styles.colors.black500};
`;

const InfoIcon = styled.span`
  display: inline-flex;
  margin-left: -2px;
  color: ${styles.colors.black300};
  cursor: help;

  &:hover, &:focus-visible { outline: none; color: ${styles.colors.primary600}; }
`;

const SwitchBtn = styled.button<{ $on: boolean }>`
  position: relative;
  width: 30px;
  height: 18px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 9px;
  background: ${({ $on }) => ($on ? styles.colors.primary600 : styles.colors.black200)};
  cursor: pointer;

  &:disabled { cursor: default; opacity: 0.6; }
`;

const SwitchKnob = styled.span<{ $on: boolean }>`
  position: absolute;
  top: 2px;
  left: ${({ $on }) => ($on ? '14px' : '2px')};
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${styles.colors.white};
  transition: left 0.15s ease;
`;

const NameEditRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  max-width: 480px;
`;

const LinkBtn = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${styles.colors.primary600};
  font-family: NunitoSans;
  font-size: 13px;
  cursor: pointer;
`;

const RemoveBtn = styled.button`
  width: 32px;
  height: 32px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 8px;
  background: transparent;
  color: ${styles.colors.black300};
  cursor: pointer;
  transition: background 0.14s, color 0.14s, border-color 0.14s;

  &:hover:not(:disabled) {
    background: ${styles.colors.red200};
    border-color: ${styles.colors.red300};
    color: ${styles.colors.red600};
  }
  &:focus-visible { outline: none; border-color: ${styles.colors.red500}; }
  &:disabled { cursor: default; opacity: 0.5; }
`;

export default SuppressionList;
